import { useState, useEffect, useCallback } from "react";
import { useErs } from "src/context/ers/ers.provider";
import { fetchEgdGames, fetchEgdJackpotMeters } from "src/api/payload/egd";
import { isPayloadSource } from "src/utils/hubSource";

/**
 * Maps a hub view to the raw Payload `gameStatus` value to filter `egd-games`
 * by, or `null` when the view doesn't need the game list at all.
 *
 * @param {import('src/api/payload/egd').EgdHubViewSlug} view
 * @returns {string | null}
 */
const gameStatusForView = (view) => {
  if (view === "progressive-jackpots") return null;
  return view === "cny" ? "cny" : "new";
};

/**
 * Unified EGD hub loader — ERS or Payload based on isPayloadSource("egd").
 *
 * @param {'en' | 'fr'} locale
 * @param {import('src/api/payload/egd').EgdHubViewSlug} view - Used to filter
 *   the Payload game-list fetch server-side (or skip it entirely for
 *   progressive-jackpots, which only needs the jackpot meters).
 * @returns {{
 *   gameList: import('src/utils/mapEgdGame').EgdGameCard[] | undefined,
 *   jackpotList: import('src/utils/mapEgdJackpot').EgdJackpotMeter[] | undefined,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export function useEgdHubData(locale, view) {
  const usePayload = isPayloadSource("egd");
  const { data: ersData, fetchData, error: ersError } = useErs();

  const [payloadGameList, setPayloadGameList] = useState([]);
  const [payloadJackpotList, setPayloadJackpotList] = useState([]);
  const [payloadLoading, setPayloadLoading] = useState(usePayload);
  const [payloadError, setPayloadError] = useState(null);
  const [ersLoading, setErsLoading] = useState(!usePayload);

  const fetchPayload = useCallback(async () => {
    if (!usePayload) return;

    setPayloadLoading(true);
    setPayloadError(null);

    try {
      const status = gameStatusForView(view);
      const [games, jackpots] = await Promise.all([
        status ? fetchEgdGames(locale, { status }) : Promise.resolve([]),
        fetchEgdJackpotMeters(locale),
      ]);
      setPayloadGameList(games);
      setPayloadJackpotList(jackpots);
    } catch (err) {
      console.error("Error fetching EGD data from Payload:", err);
      setPayloadError(err?.message || "Failed to load EGD data");
      setPayloadGameList([]);
      setPayloadJackpotList([]);
    } finally {
      setPayloadLoading(false);
    }
  }, [locale, usePayload, view]);

  useEffect(() => {
    fetchPayload();
  }, [fetchPayload]);

  useEffect(() => {
    if (usePayload) return;

    const hasGameList = Object.prototype.hasOwnProperty.call(
      ersData,
      "egd-game-list"
    );
    const hasJackpots = Object.prototype.hasOwnProperty.call(
      ersData,
      "egd-progressive-jackpots"
    );

    if (hasGameList && hasJackpots) {
      setErsLoading(false);
      return;
    }

    setErsLoading(true);

    Promise.all([
      fetchData("egd-game-list"),
      fetchData("egd-progressive-jackpots"),
    ])
      .catch((err) => {
        console.error("Error fetching EGD data from ERS:", err);
      })
      .finally(() => {
        setErsLoading(false);
      });
  }, [usePayload, fetchData, ersData]);

  if (usePayload) {
    return {
      gameList: payloadGameList,
      jackpotList: payloadJackpotList,
      loading: payloadLoading,
      error: payloadError,
    };
  }

  return {
    gameList: ersData["egd-game-list"],
    jackpotList: ersData["egd-progressive-jackpots"],
    loading: ersLoading,
    error: ersError,
  };
}
